import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of, startWith, switchMap, tap } from 'rxjs';
import { CatalogosHistoricoService, OpcionAsociacion, OpcionCargo, TIPOS_ASOCIACION } from '../../../services/catalogos-historico.service';
import { EjercicioAsociadosService, EstadoFiltro, PaginaAsociadosEjercicio, TAMANOS_PAGINA_EJERCICIO } from '../../../services/ejercicio-asociados.service';
import { ItemComponent } from '../../item/item.component';
import { ItemType } from '../../formulario/request.model';

export const DEBOUNCE_EJERCICIO_MS = 300;
const PAGE_SIZE_DEFECTO = 24;

const entero = (valor: string | null, minimo = 1): number | null => {
  const n = Number(valor);
  return valor !== null && valor !== '' && Number.isInteger(n) && n >= minimo ? n : null;
};

/**
 * Asociados de un ejercicio en tarjetas (P1.3 Wave 5). Antes la ficha descargaba de golpe ~13.000 asociados con todos sus datos;
 * ahora pide solo la página visible a GET /ejercicios/{id}/asociados?paginado=true (búsqueda por nombre, apellidos y DNI/NIF, filtros
 * por asociación, cargo, tipo de asociación y estado, y ordenación estable en MySQL). Las tarjetas muestran nombre completo y foto o
 * imagen predeterminada, más los cargos de la persona en ese ejercicio; no se muestran ni se reciben datos personales innecesarios.
 * El estado (búsqueda, filtros, página) se guarda en la URL (?q=&asociacion=&cargo=&tipo=&estado=&page=&size=) con replaceUrl.
 */
@Component({
  selector: 'app-ejercicio-asociados',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ItemComponent],
  templateUrl: './ejercicio-asociados.component.html'
})
export class EjercicioAsociadosComponent implements OnInit, OnDestroy {
  @Input({ required: true }) ejercicioId!: number;

  readonly TAMANOS = TAMANOS_PAGINA_EJERCICIO;
  readonly TIPOS = TIPOS_ASOCIACION;
  readonly ItemType = ItemType;

  readonly busqueda = new FormControl('', { nonNullable: true });
  asociaciones: OpcionAsociacion[] = [];
  cargos: OpcionCargo[] = [];
  asociacion: number | '' = '';
  cargo: number | '' = '';
  tipo: number | '' = '';
  estado: EstadoFiltro = 'todos';
  pageSize = PAGE_SIZE_DEFECTO;
  pagina = 1;

  cargando = true;
  error = '';
  resultado: PaginaAsociadosEjercicio | null = null;

  private readonly peticion$ = new Subject<void>();
  private readonly suscripciones = new Subscription();

  constructor(
    private readonly servicio: EjercicioAsociadosService,
    private readonly catalogos: CatalogosHistoricoService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  private restaurarEstado(): void {
    const p = this.route.snapshot.queryParamMap;
    this.busqueda.setValue((p.get('q') ?? '').slice(0, 100), { emitEvent: false });
    this.asociacion = entero(p.get('asociacion')) ?? '';
    this.cargo = entero(p.get('cargo')) ?? '';
    this.tipo = entero(p.get('tipo'), 0) ?? '';
    const estado = p.get('estado');
    this.estado = estado === 'vigentes' || estado === 'bajas' ? estado : 'todos';
    const tamano = Number(p.get('size'));
    this.pageSize = (TAMANOS_PAGINA_EJERCICIO as number[]).includes(tamano) ? tamano : PAGE_SIZE_DEFECTO;
    this.pagina = entero(p.get('page')) ?? 1;
  }

  private guardarEstado(): void {
    this.router.navigate([], {
      relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge',
      queryParams: {
        q: this.busqueda.value.trim() || null, asociacion: this.asociacion === '' ? null : this.asociacion, cargo: this.cargo === '' ? null : this.cargo,
        tipo: this.tipo === '' ? null : this.tipo, estado: this.estado === 'todos' ? null : this.estado,
        page: this.pagina > 1 ? this.pagina : null, size: this.pageSize !== PAGE_SIZE_DEFECTO ? this.pageSize : null
      }
    });
  }

  ngOnInit(): void {
    this.restaurarEstado();
    this.suscripciones.add(
      this.peticion$.pipe(
        startWith(undefined),
        tap(() => { this.cargando = true; this.error = ''; this.guardarEstado(); }),
        // switchMap cancela la petición anterior si el usuario cambia rápido los filtros
        switchMap(() => this.servicio.buscar(this.ejercicioId, {
          page: this.pagina, pageSize: this.pageSize, q: this.busqueda.value, asociacion: this.asociacion || null, cargo: this.cargo || null,
          tipoAsociacion: this.tipo === '' ? null : this.tipo, estado: this.estado
        }).pipe(catchError((err: HttpErrorResponse) => {
          this.error = err?.status === 403 ? 'No tienes permiso para ver los asociados del ejercicio.'
            : err?.status === 404 ? 'El ejercicio no existe.'
            : err?.error?.status?.message || err?.error?.message || 'No se han podido cargar los asociados.';
          return of(null);
        })))
      ).subscribe(res => {
        this.cargando = false;
        if (!res) { this.resultado = null; return; }
        if (!res.asociados.length && res.paginacion.total > 0 && this.pagina > res.paginacion.totalPages) {
          this.pagina = res.paginacion.totalPages;
          this.peticion$.next();
          return;
        }
        this.resultado = res;
      })
    );
    this.suscripciones.add(this.busqueda.valueChanges.pipe(debounceTime(DEBOUNCE_EJERCICIO_MS), distinctUntilChanged()).subscribe(() => this.reiniciar()));
    // Catálogos para los filtros: no bloquean las tarjetas; si fallan, los filtros quedan sin opciones
    this.suscripciones.add(this.catalogos.asociacionesOrdenadas().subscribe({ next: a => { this.asociaciones = a; }, error: () => { this.asociaciones = []; } }));
    this.suscripciones.add(this.catalogos.cargosOrdenados().subscribe({ next: c => { this.cargos = c; }, error: () => { this.cargos = []; } }));
  }

  ngOnDestroy(): void { this.suscripciones.unsubscribe(); }

  reiniciar(): void { this.pagina = 1; this.peticion$.next(); }

  irA(pagina: number): void {
    const total = this.resultado?.paginacion.totalPages ?? 1;
    this.pagina = Math.min(Math.max(1, pagina), Math.max(1, total));
    this.peticion$.next();
  }

  reintentar(): void { this.peticion$.next(); }

  limpiarFiltros(): void {
    this.asociacion = ''; this.cargo = ''; this.tipo = ''; this.estado = 'todos';
    this.busqueda.setValue('', { emitEvent: false });
    this.reiniciar();
  }

  get hayFiltros(): boolean { return this.busqueda.value.trim() !== '' || this.asociacion !== '' || this.cargo !== '' || this.tipo !== '' || this.estado !== 'todos'; }
  get total(): number { return this.resultado?.paginacion.total ?? 0; }
  get totalPaginas(): number { return this.resultado?.paginacion.totalPages ?? 0; }
  get desde(): number { return this.total ? (this.pagina - 1) * this.pageSize + 1 : 0; }
  get hasta(): number { return this.total ? Math.min(this.total, this.desde + (this.resultado?.asociados.length ?? 0) - 1) : 0; }
}
